/**
 * Trunnion AI - Execution Stream Hook
 * Real-time execution event subscription via WebSocket.
 */

import { useEffect, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { useExecutionMonitorStore } from '@/stores/executionMonitorStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { WSEvent } from '@/types';
import type {
  WSStepUpdatePayload,
  WSToolCallPayload,
  WSReasoningTracePayload,
  WSApprovalPayload,
  WSTokenUsagePayload,
} from '@/types';

export function useExecutionStream(executionId: string | null) {
  const { subscribe, emit } = useWebSocket();
  const {
    addStep,
    updateStep,
    addToolCall,
    addReasoningEntry,
    updateTokenUsage,
  } = useExecutionMonitorStore();
  const { addApprovalRequest, addNotification } = useNotificationStore();

  // Subscribe to execution events
  useEffect(() => {
    if (!executionId) return;

    emit(WSEvent.SUBSCRIBE_EXECUTION, { executionId });

    const unsubscribers = [
      subscribe<WSStepUpdatePayload>(WSEvent.EXECUTION_STEP_STARTED, (data) => {
        if (data.executionId !== executionId) return;
        addStep({
          id: data.stepId,
          nodeId: data.nodeId,
          nodeName: data.nodeName,
          nodeType: data.nodeType,
          status: data.status,
          agentId: data.agentId,
          agentName: data.agentName,
          startedAt: data.timestamp,
        });
      }),

      subscribe<WSStepUpdatePayload>(WSEvent.EXECUTION_STEP_COMPLETED, (data) => {
        if (data.executionId !== executionId) return;
        updateStep(data.stepId, {
          status: 'completed',
          output: data.output,
          completedAt: data.timestamp,
        });
      }),

      subscribe<WSStepUpdatePayload>(WSEvent.EXECUTION_STEP_FAILED, (data) => {
        if (data.executionId !== executionId) return;
        updateStep(data.stepId, {
          status: 'failed',
          error: data.error,
          completedAt: data.timestamp,
        });
      }),

      subscribe<WSToolCallPayload>(WSEvent.TOOL_CALL_STARTED, (data) => {
        if (data.executionId !== executionId) return;
        addToolCall(data.stepId, {
          id: data.toolCallId,
          toolId: data.toolId,
          toolName: data.toolName,
          status: 'running',
          parameters: data.parameters ?? {},
          startedAt: data.timestamp,
        });
      }),

      subscribe<WSToolCallPayload>(WSEvent.TOOL_CALL_COMPLETED, (data) => {
        if (data.executionId !== executionId) return;
        addToolCall(data.stepId, {
          id: data.toolCallId,
          toolId: data.toolId,
          toolName: data.toolName,
          status: 'success',
          result: data.result,
          completedAt: data.timestamp,
        });
      }),

      subscribe<WSToolCallPayload>(WSEvent.TOOL_CALL_FAILED, (data) => {
        if (data.executionId !== executionId) return;
        addToolCall(data.stepId, {
          id: data.toolCallId,
          toolId: data.toolId,
          toolName: data.toolName,
          status: 'failed',
          error: data.error,
          completedAt: data.timestamp,
        });
      }),

      subscribe<WSReasoningTracePayload>(WSEvent.REASONING_TRACE, (data) => {
        if (data.executionId !== executionId) return;
        addReasoningEntry(data.stepId, {
          id: data.traceId,
          type: data.type,
          content: data.content,
          timestamp: data.timestamp,
        });
      }),

      subscribe<WSApprovalPayload>(WSEvent.APPROVAL_REQUESTED, (data) => {
        if (data.executionId !== executionId) return;
        addApprovalRequest({
          executionId: data.executionId,
          stepId: data.stepId,
          description: data.description,
          requiredRoles: data.requiredRoles,
          expiresAt: data.expiresAt,
          timestamp: data.timestamp,
        });
        addNotification({
          type: 'warning',
          title: 'Approval Required',
          message: data.description,
          persistent: true,
        });
      }),

      subscribe<WSTokenUsagePayload>(WSEvent.TOKEN_USAGE_UPDATE, (data) => {
        if (data.executionId !== executionId) return;
        updateTokenUsage(data.stepId, {
          promptTokens: data.promptTokens,
          completionTokens: data.completionTokens,
          totalTokens: data.totalTokens,
          estimatedCost: data.estimatedCost,
        });
      }),
    ];

    return () => {
      emit(WSEvent.UNSUBSCRIBE_EXECUTION, { executionId });
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [
    executionId,
    subscribe,
    emit,
    addStep,
    updateStep,
    addToolCall,
    addReasoningEntry,
    updateTokenUsage,
    addApprovalRequest,
    addNotification,
  ]);

  const approveStep = useCallback(
    (stepId: string, justification: string) => {
      emit(WSEvent.APPROVAL_GRANTED, { executionId, stepId, justification });
    },
    [executionId, emit],
  );

  const denyStep = useCallback(
    (stepId: string, reason: string) => {
      emit(WSEvent.APPROVAL_DENIED, { executionId, stepId, reason });
    },
    [executionId, emit],
  );

  return { approveStep, denyStep };
}
